import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { type Country, type Value } from 'react-phone-number-input';

import { useAppDispatch } from '@/hooks/redux';
import { login } from '@/store/slices/authSlice';
import { useRegisterMutation, useSendOtpMutation } from '@/store/api/authApi';
import { registerSchema } from '@/utils/validation';
import { showToast } from '@/utils';
import PhoneInput, { type PhoneData } from '@/components/common/PhoneInput';
import { useOtpTimer } from '@/hooks/useOtpTimer';
import { Icons } from '@/utils/Icons';

type FormValues = z.infer<typeof registerSchema>;

const OTP_LENGTH = 6;
const DEFAULT_OTP_TIMEOUT = 60;

const DEFAULT_PHONE: PhoneData = {
  countryCode: '+91',
  country: 'IN' as Country,
  fullNumber: '',
  phoneNumber: '',
};

type Props = {
  /** Prefilled from a `?ref=` referral link. */
  referralCode?: string;
  /** Called once the account is created and the user is logged in. */
  onDone: () => void;
  /** Switch to the login form. */
  onLogin: () => void;
};

/** Signup form with mobile OTP, shown in the signup popup (`AuthModal`). */
function SignupForm({ referralCode = '', onDone, onLogin }: Props) {
  const [registerMutation, { isLoading }] = useRegisterMutation();
  const [sendOtp, { isLoading: isSendingOtp }] = useSendOtpMutation();
  const dispatch = useAppDispatch();
  const [phone, setPhone] = useState<PhoneData>(DEFAULT_PHONE);
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const {
    formattedTimeLeft,
    active,
    start: startOtpTimer,
    reset: resetOtpTimer,
  } = useOtpTimer(DEFAULT_OTP_TIMEOUT);

  const {
    register,
    control,
    handleSubmit,
    getValues,
    setValue,
    trigger,
    formState: { errors, isValid },
  } = useForm<FormValues>({
    resolver: zodResolver(registerSchema),
    mode: 'onChange',
    defaultValues: { username: '', mobile: '', password: '', referralCode: '' },
  });

  useEffect(() => {
    setValue('referralCode', referralCode, {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [referralCode, setValue]);

  const handlePhoneChange = (phoneData: PhoneData, onChange: (value: string) => void) => {
    setPhone(phoneData);
    onChange(phoneData.fullNumber);
    setOtpSent(false);
    resetOtpTimer();
    setOtp('');
  };

  const handleOtpChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setOtp(event.target.value.replace(/\D/g, '').slice(0, OTP_LENGTH));
  };

  const handleResetOtpField = () => {
    setOtpSent(false);
    resetOtpTimer();
    setOtp('');
  };

  const handleGetOtp = async () => {
    const fieldsAreValid = await trigger(['username', 'mobile', 'password', 'referralCode']);
    const mobile = getValues('mobile');

    if (!fieldsAreValid) return;

    try {
      const result = await sendOtp({
        mobile,
        country: phone.country || 'IN',
        type: 'register',
      }).unwrap();

      setOtpSent(true);
      setOtp('');
      startOtpTimer(result.mobile?.timeout);
      showToast('OTP sent to your mobile number', 'success');
    } catch (error: any) {
      const message = Array.isArray(error?.data?.message)
        ? error.data.message.join(', ')
        : (error?.data?.message ?? 'Failed to send OTP');
      showToast(message, 'error');
    }
  };

  const onSubmit = async (data: FormValues) => {
    if (otp.length !== OTP_LENGTH) {
      showToast('Please enter complete OTP', 'error');
      return;
    }

    try {
      const result = await registerMutation({
        username: data.username,
        password: data.password,
        mobile: data.mobile,
        dialCode: phone.countryCode,
        country: phone.country || 'IN',
        code: otp,
        mobileVerificationCode: otp,
        ...(data.referralCode ? { referralCode: data.referralCode } : {}),
      }).unwrap();

      dispatch(login({ type: result.type, accessToken: result.accessToken }));
      onDone();
    } catch (error: any) {
      const message = Array.isArray(error?.data?.message)
        ? error.data.message.join(', ')
        : (error?.data?.message ?? 'Registration failed');
      showToast(message, 'error');
    }
  };

  const inputWrap =
    'flex h-12 items-center gap-3 rounded-lg border border-shell-border bg-shell-raised px-4 transition focus-within:border-gold';
  const inputClass =
    'autofill-dark h-full min-w-0 flex-1 bg-transparent text-sm text-shell-fg outline-none placeholder:text-shell-muted';
  const labelClass = 'mb-1.5 block text-xs font-medium text-shell-muted';
  const errorClass = 'mt-1 text-xs text-danger-red';
  const hintClass = 'mt-1 text-xs text-shell-muted';
  const spinner = (
    <span className="size-4 animate-spin rounded-full border-2 border-current border-t-transparent" />
  );
  const otpLocked = otpSent && active;

  return (
    <div>
      <div>
        <h2 className="text-2xl font-bold text-shell-fg">Create your account</h2>
        <p className="mt-1 text-sm text-shell-muted">Sign up in a minute and start playing.</p>

        {/* Steps */}
        <ol className="mt-5 grid grid-cols-2 gap-2 text-xs font-medium">
          {['Your details', 'Verify mobile'].map((step, index) => {
            const isDone = index === 0 && otpSent;
            const isCurrent = index === (otpSent ? 1 : 0);
            return (
              <li key={step} className="flex flex-col gap-1.5">
                <span
                  className={`h-1 rounded-full ${isDone || isCurrent ? 'bg-gold' : 'bg-shell-raised'}`}
                />
                <span className={isCurrent ? 'text-shell-fg' : 'text-shell-muted'}>
                  {index + 1}. {step}
                </span>
              </li>
            );
          })}
        </ol>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-6 space-y-4">
          <div>
            <label htmlFor="signup-username" className={labelClass}>
              Username
            </label>
            <div className={inputWrap}>
              <Icons.User className="shrink-0 text-shell-muted" />
              <input
                id="signup-username"
                {...register('username')}
                onKeyDown={(e) => e.key === ' ' && e.preventDefault()}
                placeholder="Choose a username"
                autoComplete="username"
                className={inputClass}
              />
            </div>
            {errors.username ? (
              <p className={errorClass}>{errors.username.message}</p>
            ) : (
              <p className={hintClass}>4–15 letters or numbers, no spaces.</p>
            )}
          </div>

          <div>
            <span className={labelClass}>Mobile number</span>
            <Controller
              name="mobile"
              control={control}
              render={({ field }) => (
                <PhoneInput
                  value={field.value as Value}
                  onChange={(phoneData) => handlePhoneChange(phoneData, field.onChange)}
                  disabled={otpLocked}
                />
              )}
            />
            {errors.mobile && <p className={errorClass}>{errors.mobile.message}</p>}
          </div>

          <div>
            <label htmlFor="signup-password" className={labelClass}>
              Password
            </label>
            <div className={inputWrap}>
              <Icons.Lock className="shrink-0 text-shell-muted" size={13} />
              <input
                id="signup-password"
                {...register('password')}
                type={showPassword ? 'text' : 'password'}
                onKeyDown={(e) => e.key === ' ' && e.preventDefault()}
                placeholder="Create a password"
                autoComplete="new-password"
                className={inputClass}
              />
              <button
                type="button"
                onClick={() => setShowPassword((value) => !value)}
                className="shrink-0 text-shell-muted transition hover:text-shell-fg"
                aria-label={showPassword ? 'Hide password' : 'Show password'}
              >
                {showPassword ? <Icons.EyeSlash size={16} /> : <Icons.Eye size={16} />}
              </button>
            </div>
            {errors.password ? (
              <p className={errorClass}>{errors.password.message}</p>
            ) : (
              <p className={hintClass}>6–20 characters, letters and numbers.</p>
            )}
          </div>

          {/* Referral code field hidden for now. A `?ref=` link still fills the code in and it
              is still sent on signup.
          <div>
            <label htmlFor="signup-referral" className={labelClass}>
              Referral code <span className="text-shell-muted/70">(optional)</span>
            </label>
            <div className={inputWrap}>
              <Icons.Gift className="shrink-0 text-shell-muted" />
              <input
                id="signup-referral"
                {...register('referralCode')}
                placeholder="Enter referral code"
                className={inputClass}
              />
            </div>
          </div>
          */}

          {!otpSent ? (
            <>
              <button
                type="button"
                onClick={handleGetOtp}
                disabled={isSendingOtp}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-gold font-semibold text-gold-foreground transition hover:bg-gold-hover disabled:opacity-60"
              >
                {isSendingOtp && spinner}
                {isSendingOtp ? 'Sending OTP…' : 'Get OTP on mobile'}
              </button>
              <p className="flex items-start gap-2 text-xs text-shell-muted">
                <Icons.Shield className="mt-0.5 shrink-0 text-gold" />
                We&apos;ll send a one-time verification code to your mobile number by SMS.
              </p>
            </>
          ) : (
            <div className="space-y-4 rounded-xl border border-shell-border bg-shell p-4">
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs text-shell-muted">
                  Code sent to{' '}
                  <span className="font-semibold text-shell-fg">{getValues('mobile')}</span>
                </p>
                <button
                  type="button"
                  onClick={handleResetOtpField}
                  className="shrink-0 text-xs font-medium text-gold hover:underline"
                >
                  Change number
                </button>
              </div>

              <input
                value={otp}
                onChange={handleOtpChange}
                onKeyDown={(e) => e.key === ' ' && e.preventDefault()}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={OTP_LENGTH}
                placeholder="••••••"
                aria-label="6 digit OTP"
                className="h-12 w-full rounded-lg border border-shell-border bg-shell-raised text-center text-lg font-semibold tracking-[0.6em] text-shell-fg outline-none transition placeholder:text-shell-muted focus:border-gold"
              />

              <div className="text-center text-xs text-shell-muted">
                {active ? (
                  <>
                    Resend code in{' '}
                    <span className="font-semibold text-shell-fg">{formattedTimeLeft}</span>
                  </>
                ) : (
                  <button
                    type="button"
                    onClick={handleGetOtp}
                    disabled={isSendingOtp}
                    className="font-medium text-gold hover:underline disabled:opacity-60"
                  >
                    {isSendingOtp ? 'Sending OTP…' : 'Resend OTP'}
                  </button>
                )}
              </div>

              <button
                type="submit"
                disabled={isLoading || !isValid || otp.length !== OTP_LENGTH}
                className="flex h-12 w-full items-center justify-center gap-2 rounded-lg bg-gold font-semibold text-gold-foreground transition hover:bg-gold-hover disabled:opacity-60"
              >
                {isLoading && spinner}
                {isLoading ? 'Creating account…' : 'Create account'}
              </button>
            </div>
          )}
        </form>

        <p className="mt-6 text-center text-sm text-shell-muted">
          Already have an account?{' '}
          <button
            type="button"
            onClick={onLogin}
            className="font-semibold text-gold hover:underline"
          >
            Log in
          </button>
        </p>

        <p className="mt-4 text-center text-[10px] leading-relaxed text-shell-muted">
          By signing up you confirm you are 18+ and agree to play responsibly.
        </p>
      </div>
    </div>
  );
}

export default SignupForm;
